export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

export function formatDateTime(iso: string): string {
  return `${formatDate(iso)} · ${formatTime(iso)}`;
}

export function formatDateShort(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

export function formatMonthDayNumeric(iso: string): string {
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

export function dateParts(iso: string): { day: string; month: string; weekday: string } {
  const d = new Date(iso);
  return {
    day: String(d.getDate()),
    month: d.toLocaleDateString(undefined, { month: 'short' }).toUpperCase(),
    weekday: d.toLocaleDateString(undefined, { weekday: 'short' }).toUpperCase(),
  };
}

/**
 * Compact time-of-day with timezone abbreviation, e.g. "1:00 PM EDT". The
 * timezone is included because workshop times are global and a bare "1:00 PM"
 * forces the reader to guess whose clock that is. Browser-locale rendered,
 * so it always shows the viewer's TZ — never the host's.
 */
export function timeOnly(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

/**
 * The viewer's own timezone abbreviation ("EDT", "PST", "GMT+1"). Use to
 * label compact date displays where the full timezone won't fit (e.g. a date
 * tile on a card).
 */
export function viewerTzAbbr(iso: string): string {
  // toLocaleTimeString with `timeZoneName: 'short'` and no other components
  // emits e.g. "EDT" on its own. The DateTimeFormat parts API would be cleaner
  // but is less consistently supported across browsers we care about.
  const parts = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    timeZoneName: 'short',
  }).formatToParts(new Date(iso));
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
}

/**
 * "Wed, May 28 · 1:00 PM EDT (your local time)" — for the upcoming-workshop
 * detail hero, where being explicit about whose clock this is removes any
 * ambiguity. The "(your local time)" tail is the part you can't get from any
 * existing helper.
 */
export function dateTimeWithLocalNote(iso: string): string {
  return `${formatDate(iso)} · ${timeOnly(iso)} — your local time`;
}

export function isPast(iso: string): boolean {
  return new Date(iso).getTime() < Date.now();
}

export function monthYearLabel(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Group an ordered list of date-bearing items by month-year, preserving the
 * input order so the caller controls direction (descending for past, ascending
 * for upcoming).
 */
export function groupByMonth<T extends { startsAt: string }>(items: T[]): Array<{
  monthKey: string;
  monthLabel: string;
  items: T[];
}> {
  const groups: Array<{ monthKey: string; monthLabel: string; items: T[] }> = [];
  for (const item of items) {
    const d = new Date(item.startsAt);
    const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const last = groups[groups.length - 1];
    if (last && last.monthKey === monthKey) {
      last.items.push(item);
    } else {
      groups.push({
        monthKey,
        monthLabel: monthYearLabel(item.startsAt),
        items: [item],
      });
    }
  }
  return groups;
}

export function relativeFromNow(iso: string): string {
  const diff = new Date(iso).getTime() - Date.now();
  const days = Math.round(diff / (24 * 60 * 60 * 1000));
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  if (days > 1 && days < 14) return `in ${days} days`;
  if (days < -1 && days > -14) return `${Math.abs(days)} days ago`;
  if (days >= 14) {
    const weeks = Math.round(days / 7);
    return `in ${weeks} week${weeks === 1 ? '' : 's'}`;
  }
  const weeks = Math.round(Math.abs(days) / 7);
  return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
}
